import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ordersService } from '../../services/orders.service';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { catchError, forkJoin, map, of } from 'rxjs';
import { TabService } from 'src/app/shared/tab.service';
import { Router } from '@angular/router';


interface CustomerBillTripRow {
  order: any;
  trip: any;
  claim: any;
  orderNumber: string;
  tripNumber: string;
  customerName: string;
  vehicleNumber: string;
  advance: number;
  baseAmount: number;
  claimAmount: number;
  claimStatus: string;
  customerCharge: number;
  customerCharges: any[];
}

@Component({
  selector: 'app-customer-bill-popup',
  // standalone: true,
  //   imports: [
  //     CommonModule,
  //     ReactiveFormsModule,
  //     ProgressSpinnerModule
  // ],
  templateUrl: './customer-bill-popup.component.html',
  styleUrls: ['./customer-bill-popup.component.css']
})
export class CustomerBillPopupComponent {
selectedOrders: any[] = [];
  tripRows: CustomerBillTripRow[] = [];
  loading = false;
  saving = false;

  invoiceForm = this.fb.group({
    accountGstId: [null, Validators.required],
    customerAddressId: [null, Validators.required],  
    totalAmount: [0, [Validators.required, Validators.min(0)]],
    customerCharge: [0, [Validators.required, Validators.min(0)]],
    invoiceAmount: [0, [Validators.required, Validators.min(0)]],
    remarks: ['']
  });

  totals = {
    totalAmount: 0,
    customerCharge: 0,
    invoiceAmount: 0
  };
  activeChargePopupIndex: number | null = null;
  gstList: any[] = [];
  loadingGst = false;

  customerAddressList: any[] = [];
  loadingCustomerAddress = false;
  constructor(
    private fb: FormBuilder,
    private ordersService: ordersService,
    private messageService: MessageService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private tabService: TabService,
    private router: Router
  ) {
    this.selectedOrders = this.config.data?.orders || [];
  }

  ngOnInit(): void {
    this.bindInvoiceCalculations();
    this.loadCustomerBillingData();
    this.loadGstList();
    this.loadCustomerAddressList(); 
  }

  loadCustomerBillingData(): void {
    const orderIds = this.selectedOrders.map(order => order?.id).filter(Boolean);
  
    if (!orderIds.length) {
      return;
    }
  
    const filters = [{ key: 'ORDER_ID_IN', value: orderIds.join(',') }];
    this.loading = true;
  
    forkJoin({
      tripData: this.ordersService.getTripInfo(filters).pipe(
        catchError(() => of({ responseObject: [] }))
      ),
      claimData: this.ordersService.getClaimsForVendorBilling(filters).pipe(
        catchError(() => of({ responseObject: [] })) // 404 -> no claims, not an error
      ),
      additionalChargeData: this.ordersService.getAdditionalCharges(filters, 0, 100, 'id', 'ASC').pipe(
        catchError(() => of({ responseObject: [] }))
      )
    }).subscribe({
      next: ({ tripData, claimData, additionalChargeData }: any) => {
        const trips = this.extractList(tripData);
        const claims = this.extractList(claimData);
        const additionalCharges = this.extractList(additionalChargeData);
        this.tripRows = this.selectedOrders.map(order => this.mapTripRow(order, trips, claims, additionalCharges));
        this.calculateTotals();
        this.loading = false;
      },
      error: (err: any) => {
        // Should now only fire for truly unexpected failures, since each
        // inner call already handles its own errors above.
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.responseMessage || 'Failed to load customer billing details'
        });
      }
    });
  }

  generateBill(): void {
    this.recalculateInvoiceAmount();

    if (this.invoiceForm.invalid) {
      this.invoiceForm.markAllAsTouched();
      return;
    }

    const payload = this.buildCustomerBillingPayload();

    this.saving = true;
    this.ordersService.saveCustomerBilling(payload).subscribe({
      next: (res: any) => {
        this.saving = false;
        this.updateCustomerBilling(res?.responseObject)
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: res?.responseMessage || 'Customer bill created successfully'
        });
        this.ref.close(res || payload);
      },
      error: (err: any) => {
        this.saving = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.responseMessage || 'Failed to create customer bill'
        });
      }
    });
  }

  updateCustomerBilling(item: any): void {
    this.openCustomerBilling('Details', 'customer-billing/update/' + item.id, item);
  }
  private openCustomerBilling(label: string, path: string, vendorBillingData?: any): void {
    this.tabService.addTab({
      label,
      path,
      fullPath: '',
      filterData: []
    });
    this.router.navigate([path], { state: { vendorBillingData } });
  }

  isInvalid(field: string): boolean {
    const control = this.invoiceForm.get(field);
    return !!control && control.touched && control.invalid;
  }

  formatAmount(value: any): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(Number(value || 0));
  }

  getStatusClass(status: string): string {
    const value = (status || '').toUpperCase();

    if (value.includes('APPROVED') || value.includes('CLOSED') || value.includes('RECEIVED')) {
      return 'status-success';
    }

    if (value.includes('PENDING') || value.includes('DRAFT')) {
      return 'status-warning';
    }

    if (value.includes('REJECTED') || value.includes('OPEN')) {
      return 'status-danger';
    }

    return 'status-default';
  }

  private mapTripRow(order: any, trips: any[], claims: any[], additionalCharges: any[]): CustomerBillTripRow {
    const trip = trips.find(item => this.isSameId(item?.orderId || item?.order?.id, order?.id)) || {};
    const claim = claims.find(item =>
      this.isSameId(item?.orderId || item?.order?.id, order?.id) ||
      this.isSameId(item?.tripId || item?.trip?.id, trip?.id)
    ) || {};
    const customerCharges = additionalCharges.filter(item => {
      const matchesOrder = this.isSameId(item?.orderId || item?.order?.id, order?.id);
      const matchesTrip = this.isSameId(item?.tripId || item?.trip?.id, trip?.id);
      return (matchesOrder || matchesTrip) && this.isTruthy(item?.chargeToCustomer);
    });
    const customerCharge = customerCharges.reduce((sum, item) => {
      return sum + this.toNumber(item?.charge);
    }, 0);

    return {
      order,
      trip,
      claim,
      orderNumber: order?.orderNumber || order?.orderNo || `ORD-${order?.id}`,
      tripNumber: trip?.tripNumber || trip?.tripNo || order?.tripNumber || '--',
      customerName: order?.customerName || order?.customer?.name || trip?.customerName || '--',
      vehicleNumber: trip?.vehicleNumber || trip?.vehicleNo || trip?.vehicle || '--',
      advance: this.toNumber(trip?.advance || trip?.advanceAmount || order?.advance || order?.advanceAmount),
      baseAmount: this.toNumber(order?.confirmedAmount || 0),
      claimAmount: this.toNumber(claim?.totalAmount || claim?.claimAmount || claim?.amount),
      claimStatus: claim?.status || claim?.claimStatus || 'No Claim',
      customerCharge,
      customerCharges
    };
  }

  private calculateTotals(): void {
    this.totals = this.tripRows.reduce((total, row) => {
      total.totalAmount += row.baseAmount;
      total.customerCharge += row.customerCharge;
      return total;
    }, { totalAmount: 0, customerCharge: 0, invoiceAmount: 0 });

    this.totals.invoiceAmount = this.totals.totalAmount + this.totals.customerCharge;

    this.invoiceForm.patchValue({
      totalAmount: this.totals.totalAmount,
      customerCharge: this.totals.customerCharge,
      invoiceAmount: this.totals.invoiceAmount
    }, { emitEvent: false });
  }

  private bindInvoiceCalculations(): void {
    ['totalAmount', 'customerCharge'].forEach(field => {
      this.invoiceForm.get(field)?.valueChanges.subscribe(() => this.recalculateInvoiceAmount());
    });
  }

  // Total Payable = Total Amount + Customer Charge
  private recalculateInvoiceAmount(): void {
    const totalAmount = this.toNumber(this.invoiceForm.get('totalAmount')?.value);
    const customerCharge = this.toNumber(this.invoiceForm.get('customerCharge')?.value);
    const invoiceAmount = totalAmount + customerCharge;

    this.totals.totalAmount = totalAmount;
    this.totals.customerCharge = customerCharge;
    this.totals.invoiceAmount = invoiceAmount;

    this.invoiceForm.patchValue({ invoiceAmount }, { emitEvent: false });
  }

  private buildCustomerBillingPayload(): any {
    const firstOrder = this.selectedOrders[0] || {};
    return {
      customerId: firstOrder?.customerId || firstOrder?.customer?.id || null,
      accountId: firstOrder?.accountId || firstOrder?.account?.id || null,
      accountGstId: this.invoiceForm.value.accountGstId || null,        
      customerAddressId: this.invoiceForm.value.customerAddressId || null,
      invoiceDate: this.formatInvoiceDate(new Date()),
      totalAmount: this.toNumber(this.invoiceForm.value.totalAmount),
      invoiceAmount: this.toNumber(this.invoiceForm.value.invoiceAmount),
      status: 'CREATED',
      paymentStatus: 'PENDING',
      remarks: this.invoiceForm.value.remarks || '',
      tripIds: this.tripRows.map(row => row.trip?.id).filter(Boolean)
    };
  }

  private formatInvoiceDate(value: any): string {
    if (!value) {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const pad = (num: number): string => String(num).padStart(2, '0');
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absOffset = Math.abs(offsetMinutes);
    const offset = `${sign}${pad(Math.floor(absOffset / 60))}${pad(absOffset % 60)}`;

    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())} ${offset}`;
  }

  private extractList(response: any): any[] {
    const responseObject = response?.responseObject;
    const list = responseObject?.content || responseObject || response?.content || response || [];
    return Array.isArray(list) ? list : [list];
  }

  private isSameId(left: any, right: any): boolean {
    return left !== null && left !== undefined && right !== null && right !== undefined && String(left) === String(right);
  }

  private toNumber(value: any): number {
    return Number(value || 0);
  }

  private isTruthy(value: any): boolean {
    return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
  }

  toggleChargePopup(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.activeChargePopupIndex = this.activeChargePopupIndex === index ? null : index;
  }
  
  closeChargePopup(): void {
    this.activeChargePopupIndex = null;
  }
  
  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeChargePopup();
  }
  
  getChargeLabel(item: any): string {
    return item?.chargeType || item?.chargeName || item?.type || item?.description || 'Charge';
  }

  getChargeRemarks(item: any): string {
    return item?.remarks || item?.remark || item?.notes || '';
  }

  private loadGstList(): void {
    const accountId = this.selectedOrders[0]?.accountId || this.selectedOrders[0]?.account?.id;
  
    if (!accountId) {
      return;
    }
  
    this.loadingGst = true;
  
    this.ordersService.getByAccountId(accountId)
      .pipe(
        map((res: any) => this.extractList(res)),
        catchError(err => {
          console.error('Failed to load GST list', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load GST details'
          });
          return of([]);
        })
      )
      .subscribe((list: any[]) => {
        this.gstList = list;
        this.loadingGst = false;
      });
  }
  
  private loadCustomerAddressList(): void {
    const customerId = this.selectedOrders[0]?.customerId || this.selectedOrders[0]?.customer?.id;
  
    if (!customerId) {
      return;
    }
  
    this.loadingCustomerAddress = true;
  
    const body = [{ key: 'CUSTOMER_ID', value: String(customerId) }];
  
    this.ordersService.getCustomerAddress(body).pipe(
      map((res: any) => this.extractList(res)),
      catchError(err => {
        console.error('Failed to load customer address list', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load customer addresses'
        });
        return of([]);
      })
    ).subscribe((list: any[]) => {
      this.customerAddressList = list;
      this.loadingCustomerAddress = false;
    });
  }
  
  getGstLabel(gst: any): string {
    return gst?.gstNumber || gst?.gstNo || gst?.gstin || gst?.name || '--';
  }
  
  getCustomerAddressLabel(address: any): string {
    return address?.addressLine1 || address?.address || address?.fullAddress || address?.name || '--';
  }

}